import {Component, OnInit, OnDestroy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {PlacementService} from '@services/placement.service';
import {MedicationService} from '@services/medication.service';
import {TabCommunicationService} from '@services/tab-communication.service';
import {InsightsPanelComponent as CycleInsightsPanelComponent} from '@components/cycle/insights-panel/insights-panel.component';
import {Subscription} from 'rxjs';
import {ToastrService} from 'ngx-toastr';

@Component({
    selector: 'app-placement-detail',
    templateUrl: './placement-detail.component.html',
    styleUrl: './placement-detail.component.scss'
})
export class PlacementDetailComponent implements OnInit, OnDestroy {
    // Expose the standalone cycle insights component for dynamic render
    public cycleInsights = CycleInsightsPanelComponent;
    public placementId: number;
    public placement: any;
    public mortalities: any;
    public medications: any;
    public vaccines: any;
    public averageWeights: any;
    public odoAverageweights: any;
    public performance: any;
    public summaryDetails: any = {
        cycleAge: 0,
        bridCount: 0,
        avgWeight: 0,
        density: 0
    };
    public mortalitySummary: any = {
        totalMortalities: 0,
        mortalityRate: 0,
        todayMortalities: 0,
        weeklyMortalities: 0
    };
    public showMedicationPopup: boolean = false;
    public showMortalityPopup: boolean = false;
    public showVaccinationPopup: boolean = false;
    public showBodyWeightPopup: boolean = false;
    public activeTab: string = 'general';
    private tabSubscription: Subscription;

    public openMedicationPopupHandler = () => {
        this.openMedicationPopup();
    };

    public openMortalityPopupHandler = () => {
        this.openMortalityPopup();
    };

    public openVaccinationPopupHandler = () => {
        this.openVaccinationPopup();
    };

    public openBodyWeightPopupHandler = () => {
        this.openBodyWeightPopup();
    };

    //medication static values
    brandOptions = [
        {id: 1, name: 'Brand A'},
        {id: 2, name: 'Brand B'},
        {id: 3, name: 'Brand C'}
    ];

    typeOptions = [
        {id: 1, name: 'Type 1'},
        {id: 2, name: 'Type 2'},
        {id: 3, name: 'Type 3'}
    ];

    classificationOptions = [
        {id: 1, name: 'Classification 1'},
        {id: 2, name: 'Classification 2'},
        {id: 3, name: 'Classification 3'}
    ];

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private placementService: PlacementService,
        private medicationService: MedicationService,
        private tabCommunicationService: TabCommunicationService,
        private toastr: ToastrService
    ) {}
    ngOnInit(): void {
        this.placementId = this.route.snapshot.params['id'];

        this.tabSubscription =
            this.tabCommunicationService.activeTab$.subscribe(
                (tabId: string) => {
                    this.activeTab = tabId;
                }
            );

        // Load medication dropdown data
        this.loadClassificationTypes();
        this.loadApplicationTypes();

        this.getPlacementDetails(this.placementId);
        this.getSummaryDetailsByPlacementId(this.placementId);
        this.getTotalMortalities(this.placementId);
        this.getActivePlacementMortalitylistByPlacementId(this.placementId);
        this.getActivePlacementMedicationlistByPlacementId(this.placementId);
        this.getActivePlacementVaccinelistByPlacementId(this.placementId);
        this.getActivePlacementAveragelistByPlacementId(this.placementId);
        this.getCyclePerformanceMetrics(this.placementId);
    }

    getCyclePerformanceMetrics(placementId: number) {
        this.placementService
            .getCyclePerformanceMetrics(placementId)
            .subscribe({
                next: (response) => {
                    this.performance = response;
                },
                error: () => {}
            });
    }

    loadClassificationTypes(): void {
        this.medicationService.getClassificationList().subscribe({
            next: (response) => {
                this.classificationOptions = response;
            }
        });
    }

    loadApplicationTypes(): void {
        this.medicationService.getApplicationTypeList().subscribe({
            next: (response) => {
                this.typeOptions = response;
            }
        });
    }
    getPlacementDetails(id: number): void {
        this.placementService
            .getPlacementDetailById(id)
            .subscribe((response) => {
                this.placement = response;
            });
    }
    getSummaryDetailsByPlacementId(placementId: number): void {
        this.placementService
            .getSummaryDetailsByPlacementId(placementId)
            .subscribe({
                next: (response) => {
                    this.summaryDetails = response;
                },
                error: (error) => {
                    this.toastr.error('Failed to load summary details.');
                }
            });
    }

    getTotalMortalities(placementId: number): void {
        this.placementService.getTotalMortalities(placementId).subscribe({
            next: (response) => {
                this.mortalitySummary = response;
            },
            error: () => {}
        });
    }

    getActivePlacementMortalitylistByPlacementId(placementId: number): void {
        this.placementService
            .getActivePlacementMortalitylistByPlacementId(placementId)
            .subscribe((response) => {
                this.mortalities = response;
            });
    }
    getActivePlacementMedicationlistByPlacementId(placementId: number): void {
        this.placementService
            .getActivePlacementMedicationlistByPlacementId(placementId)
            .subscribe((response) => {
                this.medications = this.mapMedicationData(response || []);
            });
    }

    mapMedicationData(medications: any[]): any[] {
        return medications.map((item) => ({
            ...item,
            // Application Type (replacing Method)
            applicationType: item.method || '',

            typeName: this.getOptionName(this.typeOptions, item.typeId),
            classificationName: this.getOptionName(
                this.classificationOptions,
                item.classiFicationId
            )
        }));
    }
    // Helper function to get name from options
    getOptionName(options: {id: number; name: string}[], id: number): string {
        const option = options.find((opt) => opt.id === id);
        return option ? option.name : '';
    }
    //vaccines
    getActivePlacementVaccinelistByPlacementId(placementId: number): void {
        this.placementService
            .GetActivePlacementVaccinationRecords(placementId)
            .subscribe((response) => {
                this.vaccines = response;
            });
    }

    getActivePlacementAveragelistByPlacementId(placementId: number): void {
        this.placementService
            .getWeeklyBodyWeightMetricsList(placementId)
            .subscribe({
                next: (response) => {
                    this.averageWeights = response;
                },
                error: () => {}
            });
    }

    openMedicationPopup(): void {
        this.showMedicationPopup = true;
    }

    closeMedicationPopup(): void {
        this.showMedicationPopup = false;
    }

    openMortalityPopup(): void {
        this.showMortalityPopup = true;
    }

    closeMortalityPopup(): void {
        this.showMortalityPopup = false;
    }

    openVaccinationPopup(): void {
        this.showVaccinationPopup = true;
    }

    closeVaccinationPopup(): void {
        this.showVaccinationPopup = false;
    }

    openBodyWeightPopup(): void {
        this.showBodyWeightPopup = true;
    }

    closeBodyWeightPopup(): void {
        this.showBodyWeightPopup = false;
    }

    onMortalityDataSaved(): void {
        this.showMortalityPopup = false;
        this.getActivePlacementMortalitylistByPlacementId(this.placementId);
        this.getTotalMortalities(this.placementId);
    }

    onMedicationDataSaved(): void {
        this.showMedicationPopup = false;
        this.getActivePlacementMedicationlistByPlacementId(this.placementId);
    }

    onVaccinationDataSaved(): void {
        this.showVaccinationPopup = false;
        this.getActivePlacementVaccinelistByPlacementId(this.placementId);
    }

    onBodyWeightDataSaved(): void {
        this.showBodyWeightPopup = false;
        this.getActivePlacementAveragelistByPlacementId(this.placementId);
        this.getSummaryDetailsByPlacementId(this.placementId);
    }

    isMedicationTabActive(): boolean {
        return this.activeTab === 'medication';
    }

    isMortalityTabActive(): boolean {
        return this.activeTab === 'mortality';
    }

    isVaccinationTabActive(): boolean {
        return this.activeTab === 'vaccination';
    }

    isBodyWeightTabActive(): boolean {
        return this.activeTab === 'body-weight';
    }

    ngOnDestroy(): void {
        if (this.tabSubscription) {
            this.tabSubscription.unsubscribe();
        }
    }
}
